import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import styles from 'components/podiatry/page.module.scss';
import DoneIcon from 'assets/svg/DoneIcon';
import VerticalLineIcon from 'assets/svg/VerticalLineIcon';
import StateCircleIcon from 'components/common/stateCircleIcon';
import { useDispatch } from 'react-redux';
import { ModuleTypes } from 'types/appState';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { HpiFromIntakeElementType } from 'types/hpi';
import { actionTypesOfReasonForVisit } from 'reducer/actionType';
import Spinner from 'components/spinner/Spinner';
import { useAddReasonForVisit } from 'hooks/useApi';
import {
  REASON_FOR_VISIT_KEY,
  StateTypeOfReasonForVisit,
  usePersistRFVReducer,
} from 'reducer/reasonForVisit';
import axios from 'axios';
import {
  clearLocalStorage,
  removeItemFromLocalStorage,
  parseErrorMessage,
} from 'utils/helper';
import { entryScreenContent } from 'templates/entryScreen';
import MetaTag from 'components/meta';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import useCommuniqueState from 'hooks/useCommuniqueState';
import { appointmentIntakeStatusAction } from 'reduxStateSlice/appointment';

enum OptionName {
  other = 'Other',
}

type Props = {
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

const title = 'Please, complete the following';

function PageResult({ nextOrSubmitButton }: Props) {
  const dispatch = useDispatch();
  const { setCommuniqueData, communiqueData } = useCommuniqueState();
  const { mutateAsync: addReasonForVisit } = useAddReasonForVisit();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [state] = usePersistRFVReducer();

  const handleAddReasonForVisit = useCallback(async () => {
    const reasonForVisitCont = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.REASON_FOR_VISIT_CONT,
    );
    const reasonForVisitContText = reasonForVisitCont.value.map((value: string) => (value === 'Other' ? `${value} - ${reasonForVisitCont.text}` : value)).join(', ');

    const chiefComplainTwo = state.find(
      (p: StateTypeOfReasonForVisit) => p.name === actionTypesOfReasonForVisit.WHICH_FOOT,
    );
    const onSet = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.HOW_LONG_EXPERIENCING_PROBLEM,
    );

    const persistent = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.CONSISTENTLY_OR_OFF_PROBLEM,
    );
    const canNoLonger = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
        === actionTypesOfReasonForVisit.WHICH_OF_THE_FOLLOWING_CAN_YOU_NO_LONGER,
    );

    const cause = state.find(
      (p: StateTypeOfReasonForVisit) => p.name === actionTypesOfReasonForVisit.HOW_DID_IT_START,
    );

    const causeArray = cause.value.map((value: string) => (value === 'Other' ? `${value} - ${cause.text}` : value));

    const qualityPain = state.find(
      (p: StateTypeOfReasonForVisit) => p.name === actionTypesOfReasonForVisit.WHAT_TYPE_OF_PAIN,
    );
    const symptoms = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.WHAT_ARE_YOUR_SYMPTOMS,
    );
    const mostSever = state.find(
      (p: StateTypeOfReasonForVisit) => p.name === actionTypesOfReasonForVisit.PAIN_WORST,
    );

    const homeTreatments = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.TREATMENTS_TRIED_HOME,
    );
    const otherProviders = state.find(
      (p: StateTypeOfReasonForVisit) => p.name === actionTypesOfReasonForVisit.SEEN_OTHER_PROVIDERS,
    );
    const providerTreatments = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.WHAT_TREATMENTS_DID_PROVIDER,
    );

    const physicalTherapy = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.HOW_LONG_DID_PHYSICAL_THERAPY,
    );

    const helpfulTreatments = state.find(
      (p: StateTypeOfReasonForVisit) => p.name
      === actionTypesOfReasonForVisit.WHAT_TREATMENTS_WERE_HELPFUL,
    );

    const helpfulTreatmentsArray = helpfulTreatments.value.map((value: string) => (value === 'Other' ? `${value} - ${helpfulTreatments.text}` : value));

    // qualityPain HPI Attributes
    const qualityPainAttributes: string[] = qualityPain.value.map(
      (item: string) => (item === OptionName.other
        ? `${item} - ${qualityPain.text}`
        : item),
    );

    // symptoms HPI Attributes
    const symptomsAttributes: string[] = symptoms.value.map(
      (item: string) => (item === OptionName.other
        ? `${item} - ${symptoms.text}`
        : item),
    );

    // mostSever HPI Attributes
    const mostSeverAttributes: string[] = mostSever.value.map(
      (item: string) => (item === OptionName.other
        ? `${item} - ${mostSever.text}`
        : item),
    );

    // mostSever HPI Attributes
    const homeTreatmentsAttributes: string[] = homeTreatments.value.map(
      (item: string) => (item === OptionName.other
        ? `${item} - ${homeTreatments.text}`
        : item),
    );

    // providerTreatments HPI Attributes
    const providerTreatmentsAttributes: string[] = providerTreatments.value.map(
      (item: string) => {
        const option = providerTreatments.options.find(
          (i: { name: string; text: string }) => i.name === item,
        );

        return isNil(option) ? item : `${option?.name} - ${option?.text}`;
      },
    );

    const hpiPayload: HpiFromIntakeElementType[] = [
      {
        title: 'Chief Complaint',
        hpiAttributes: [
          reasonForVisitContText,
          chiefComplainTwo?.value.toString(),
        ],
      },
      {
        title: 'Onset',
        hpiAttributes: onSet?.value,
      },
      {
        title: 'Persistence',
        hpiAttributes: [
          persistent.value.toString() === 'Consistently'
            ? 'Persistent'
            : 'Intermittent',
        ],
      },
      {
        title: 'Cause',
        hpiAttributes: causeArray,
      },
      {
        title: 'Quality',
        hpiAttributes: qualityPainAttributes,
      },
      {
        title: 'Can no longer',
        hpiAttributes: canNoLonger.value,
      },
      {
        title: 'Pain',
        hpiAttributes: [qualityPain.painRate.toString()],
      },
      {
        title: 'Symptoms',
        hpiAttributes: symptomsAttributes,
      },
      {
        title: 'Most severe',
        hpiAttributes: mostSeverAttributes,
      },
      {
        title: 'Home treatments',
        hpiAttributes: homeTreatmentsAttributes,
      },
      {
        title: 'Other providers',
        hpiAttributes: otherProviders.value,
      },
    ];

    // Push where on the foot to hipPayload
    // if (whereOnTheFoot.value.length) {
    //   hpiPayload.push({
    //     title: "Where on foot",
    //     hpiAttributes: whereOnTheFoot.value,
    //   });
    // }

    if (otherProviders.value.toString() === 'Yes') {
      hpiPayload.push(
        {
          title: 'Provider treatments',
          hpiAttributes: providerTreatmentsAttributes,
        },
        {
          title: 'Helpful treatments',
          hpiAttributes: helpfulTreatmentsArray,
        },
      );
    }

    if (physicalTherapy.value.toString() !== '') {
      hpiPayload.push({
        title: 'PT Duration',
        hpiAttributes: physicalTherapy.value,
      });
    }

    const payload = {
      encounterId: communiqueData?.appointmentId,
      visitDateTimeOffset: dayjs().format(),
      hpiFromIntake: {
        hpiFromIntakeElements: hpiPayload,
      },
    };

    try {
      await addReasonForVisit(payload);
      // Clear cache for reason for visit
      removeItemFromLocalStorage(REASON_FOR_VISIT_KEY);
      if (!isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          sendForms: {
            ...communiqueData?.sendForms,
            hpi: false,
          },
        });
      }
      dispatch(appointmentIntakeStatusAction({ hpiSubmitted: true }));

      if (communiqueData?.sendForms?.medicalHistory) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.medicalHistory,
            page: null,
          }),
        );
      } else if (communiqueData?.sendForms?.consentForm) {
        dispatch(
          appStateSettingsAction({
            name: ModuleTypes.consentForm,
            page: null,
          }),
        );
      } else {
        clearLocalStorage();
        nextOrSubmitButton(200); // 200 for complete page of Reason for visit
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage('Something went wrong on AddReasonForVisit');
        console.error('🚀 ~ error on submit AddReasonForVisit : ', error);
      }
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    handleAddReasonForVisit();
  }, []);

  return (
    <>
      <MetaTag
        title={
          // eslint-disable-next-line no-nested-ternary
          isLoading
            ? 'Submitting reason for visit...'
            : errorMessage === ''
              ? title
              : errorMessage
        }
      />
      <Box pb={1}>
        {!isLoading && errorMessage === '' ? (
          <Box mt={3} minHeight="72vh">
            <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>

            <Box className={styles.startInstructionItem} mt={3}>
              <DoneIcon />
              <Typography paragraph className={styles.label}>
                {entryScreenContent.reason_for_visit}
              </Typography>
            </Box>
            <Box className={styles.resultPageVerticalLine}>
              <VerticalLineIcon />
            </Box>

            <Box className={styles.startInstructionItem}>
              <StateCircleIcon value="2" isCurrent />
              <Typography paragraph className={styles.label}>
                {entryScreenContent.medical_history}
              </Typography>
            </Box>
            <Box className={styles.resultPageVerticalLine}>
              <VerticalLineIcon />
            </Box>

            <Box className={styles.startInstructionItem}>
              <StateCircleIcon value="3" isCurrent={false} />
              <Typography paragraph className={styles.label}>
                {entryScreenContent.acknowledge_sign}
                <br />
                <Typography component="span" className={styles.labelSubtext}>
                  {entryScreenContent.acknowledge_sign_desc}
                </Typography>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            mt={3}
            minHeight="72vh"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {errorMessage ? (
              <p className="error-message w-full">{errorMessage}</p>
            ) : (
              <Spinner />
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

export default PageResult;
